/**
 * Currency formatting and parsing utilities
 * Handles Brazilian Real (BRL) formatting
 */

/**
 * Format a number to currency string (R$ 1.234,56)
 * @param value - Number to format
 * @param currency - Currency code (default: BRL)
 * @returns Formatted currency string
 */
export const formatCurrency = (value: number, currency: string = 'BRL'): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency,
  }).format(value);
};

/**
 * Parse a currency string to number
 * Accepts formats like: "R$ 1.234,56", "1.234,56", "1234,56", "1234.56"
 * @param value - Currency string to parse
 * @returns Parsed number or null if invalid
 */
export const parseCurrency = (value: string): number | null => {
  if (!value || value.trim() === '') return null;

  // Remove currency symbols and whitespace
  let cleanValue = value
    .replace(/R\$/g, '')
    .replace(/\s/g, '')
    .trim();

  // Replace comma with dot for decimal separator
  cleanValue = cleanValue.replace(/\./g, '').replace(',', '.');

  const parsed = parseFloat(cleanValue);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Format input value as user types (live formatting)
 * @param value - Current input value
 * @returns Formatted string for display
 */
export const formatCurrencyInput = (value: string): string => {
  if (!value || value === '') return '';

  // Remove all non-numeric characters except comma and dot
  const numericValue = value.replace(/[^\d,]/g, '');

  // Parse to number
  const parsed = parseCurrency(numericValue);
  if (parsed === null) return '';

  // Format without currency symbol for input
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(parsed);
};

/**
 * Validate if a string is a valid currency input
 * @param value - String to validate
 * @returns True if valid currency format
 */
export const isValidCurrencyInput = (value: string): boolean => {
  if (!value || value.trim() === '') return true; // Empty is valid
  const parsed = parseCurrency(value);
  return parsed !== null && parsed >= 0;
};
